// components/CompanyImageUploadModal.js
import { useState, useRef } from 'react'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { app } from '../firebase'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import CloseIcon from '@mui/icons-material/Close'
import toast from 'react-hot-toast'
import './CompanyImageUploadModal.css'

/**
 * Universal modal component for uploading company images (header, logo, etc.)
 * @param {Object} props
 * @param {boolean} props.open - Whether the modal is open
 * @param {Object} props.company - Company object with id and name
 * @param {string} props.imageType - Type of image ('header' or 'logo')
 * @param {Function} props.onClose - Callback when modal is closed
 * @param {Function} props.onSuccess - Callback when upload succeeds (optional)
 */
function CompanyImageUploadModal ({ open, company, imageType, onClose, onSuccess }) {
  const [selectedFile, setSelectedFile] = useState(null)
  const [previewUrl, setPreviewUrl] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState(null)
  const fileInputRef = useRef(null)

  const functions = getFunctions(app)

  if (!open || !company || !imageType) return null

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Bitte wählen Sie ein Bild aus')
      return
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Das Bild ist zu groß. Maximal 10MB erlaubt.')
      return
    }

    setSelectedFile(file)
    setError(null)
    
    // Create preview URL
    const reader = new FileReader()
    reader.onloadend = () => {
      setPreviewUrl(reader.result)
    }
    reader.readAsDataURL(file)
  }

  const handleUpload = async () => {
    if (!selectedFile || !company) return

    setUploading(true)
    setError(null)

    try {
      const companyId = company.id
      
      // Determine file extension from MIME type or filename
      let extension = 'jpg'
      if (selectedFile.type === 'image/png') extension = 'png'
      else if (selectedFile.type === 'image/webp') extension = 'webp'
      else if (selectedFile.type === 'image/jpeg') extension = 'jpg'
      else if (selectedFile.name) {
        const match = selectedFile.name.match(/\.(jpg|jpeg|png|webp)$/i)
        if (match) extension = match[1].toLowerCase()
      }

      const fileName = `${imageType}.${extension}`

      // Convert file to base64
      const reader = new FileReader()
      const imageData = await new Promise((resolve, reject) => {
        reader.onloadend = () => resolve(reader.result)
        reader.onerror = reject
        reader.readAsDataURL(selectedFile)
      })

      // Upload via Cloud Function
      const uploadCompanyImage = httpsCallable(functions, 'uploadCompanyImage')
      const result = await uploadCompanyImage({
        companyId,
        imageData,
        fileName,
        contentType: selectedFile.type,
        imageType
      })

      if (result.data.success) {
        toast.success(result.data.message)
        if (onSuccess) {
          onSuccess()
        }
        handleClose()
      } else {
        throw new Error(result.data.message || 'Fehler beim Hochladen des Bildes')
      }
    } catch (error) {
      console.error('Error uploading image:', error)
      setError(error.message || 'Fehler beim Hochladen des Bildes')
      toast.error(error.message || 'Fehler beim Hochladen des Bildes')
    } finally {
      setUploading(false)
    }
  }

  const handleClose = () => {
    setSelectedFile(null)
    setPreviewUrl(null)
    setError(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
    onClose()
  }

  const getImageTypeLabel = () => {
    return imageType === 'header' ? 'Header' : 'Logo'
  }

  return (
    <div className='modal-overlay' onClick={handleClose}>
      <div className='modal-content company-image-upload-modal' onClick={e => e.stopPropagation()}>
        <div className='modal-header'>
          <h2>{getImageTypeLabel()}-Bild hochladen</h2>
          <button
            className='btn-close-modal'
            onClick={handleClose}
            disabled={uploading}
          >
            <CloseIcon />
          </button>
        </div>

        <div className='modal-body'>
          <div className='upload-info'>
            <p>
              <strong>Company:</strong> {company.name}
            </p>
            <p className='upload-hint'>
              Laden Sie ein Bild hoch, das als {getImageTypeLabel().toLowerCase()} für diese Company verwendet wird.
              Maximal 10MB, Formate: JPG, PNG, WebP
            </p>
          </div>

          <div className='upload-area'>
            <input
              ref={fileInputRef}
              type='file'
              accept='image/jpeg,image/jpg,image/png,image/webp'
              onChange={handleFileSelect}
              disabled={uploading}
              style={{ display: 'none' }}
            />
            
            {!selectedFile ? (
              <button
                className='btn-upload-select'
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                <CloudUploadIcon />
                Bild auswählen
              </button>
            ) : (
              <div className='preview-container'>
                {previewUrl && (
                  <img
                    src={previewUrl}
                    alt='Preview'
                    className='preview-image'
                  />
                )}
                <div className='preview-actions'>
                  <button
                    className='btn-change-image'
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                  >
                    Anderes Bild wählen
                  </button>
                </div>
              </div>
            )}
          </div>

          {error && (
            <div className='error-message'>
              <span>{error}</span>
            </div>
          )}
        </div>

        <div className='modal-actions'>
          <button
            className='btn-cancel'
            onClick={handleClose}
            disabled={uploading}
          >
            Abbrechen
          </button>
          <button
            className='btn-primary'
            onClick={handleUpload}
            disabled={!selectedFile || uploading}
          >
            {uploading ? 'Lädt hoch...' : 'Hochladen'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default CompanyImageUploadModal

